import { Injectable } from '@angular/core';
import { ChatmodelsService } from './chatmodels.service';

@Injectable({
  providedIn: 'root'
})
export class OpenRouterService {

  constructor(private chatmodelsService: ChatmodelsService) {}

  /**
   * Generates / enhances text via the backend AI proxy.
   *
   * The backend:
   *   1. Reads the active provider configuration from the database (for this tenant).
   *   2. Decrypts the stored API key server-side.
   *   3. Calls the configured AI provider (e.g. OpenRouter).
   *   4. Returns only the generated text.
   *
   * No API keys or provider secrets are ever sent to the browser.
   *
   * @param customPrompt   The instruction / task prompt (e.g. "Make this more professional").
   * @param textToEnhance  The source text to be enhanced. Pass an empty string for pure generation.
   * @param providerId     Optional: target a specific saved provider by ID.
   *                       If omitted, the backend uses the first configured provider for the tenant.
   */
  async generateText(
    customPrompt: string,
    textToEnhance: string,
    providerData?: any
  ): Promise<string> {
    // Build the full prompt that the backend will forward to the AI model.
    let fullPrompt: string;
    if (textToEnhance && textToEnhance.trim()) {
      fullPrompt =
        `${customPrompt}\n\nTarget Text:\n"""\n${textToEnhance}\n"""\n\n` +
        `Remember: Return ONLY the enhanced resulting text. ` +
        `Do not include any explanations, introductory text, conversational preamble, ` +
        `or wrapper markdown blocks like \`\`\`html or \`\`\`.`;
    } else {
      fullPrompt =
        `${customPrompt}\n\n` +
        `Remember: Return ONLY the resulting text. ` +
        `Do not include any explanations, introductory text, conversational preamble, ` +
        `or wrapper markdown blocks like \`\`\`html or \`\`\`.`;
    }

    // Delegate to the backend proxy — no key handling on the frontend.
    const response = await this.chatmodelsService
      .aiEnhance(fullPrompt, providerData)
      .toPromise();

    if (!response) {
      throw new Error('No response received from AI enhancement service.');
    }

    // Check for backend-reported errors
    if (response.status_code && response.status_code !== 'AST-200') {
      throw new Error(response.status_message || 'AI enhancement failed.');
    }

    // Extract the generated text from the nested response structure
    const generatedText: string =
      response?.data?.generatedText ??
      response?.datalist?.[0]?.generatedText ??
      response?.generatedText ??
      '';

    if (!generatedText) {
      throw new Error('AI provider returned an empty response.');
    }

    // Strip any markdown code-block wrappers the model may have added
    let text = generatedText;
    if (text.startsWith('```')) {
      text = text.replace(/^```[a-zA-Z]*\n?/, '').replace(/\n?```$/, '');
    }

    return text.trim();
  }
}
